import fetch from '@ixigo/fetch';
import { executeApiCall } from './helper';
import type {
	ApiErrorResponse,
	BaseCabItemOption,
	BaseContactDetail,
	BaseFarePolicyDetail,
	BaseFareSummary,
	RideDetails,
} from './types';

export interface Heading {
	title: string;
	subtitle?: string;
}

export type BookingStatus =
	| 'CONFIRMED'
	| 'COMPLETED'
	| 'CANCELLED'
	| 'RESCHEDULED' // This status is only for UI usecase, it will not come from backend
	| 'PAYMENT_PENDING'
	| 'PAYMENT_SUCCESS_BOOKING_PENDING'
	| 'FAILED';

export interface WaitingInfo {
	status: 'info' | 'warning' | 'critical';
	timerValueInMs: number;
	showTimer: boolean;
}

export type RideStatus =
	| 'CAB_ASSIGNED'
	| 'CAB_ARRIVED'
	| 'TRIP_STARTED'
	| 'TRIP_ENDED';

export interface CabUpdateResponse {
	msg: string;
	stopPolling: boolean;
	pollingIntervalMs: number;
	trackingUrl?: string;
	tripActivationTime: {
		time: string; // ISO date string
		timezone: string;
	};
	waitingInfo?: WaitingInfo;
	isTripEnded?: boolean;
	cab: {
		imageUrl: string;
		name: string;
		licenseNumber: string;
	};
	driver?: {
		imageUrl: string;
		name: string;
		phoneNumber: string;
	};
	pin?: {
		label: string;
		value: string;
	};
	rideStatus?: RideStatus;
	pricingInfo?: {
		amountToBePaid: BaseFareSummary;
		displayFares: BaseFareSummary[];
	};
	emergencyContact?: EmergencyContact;
}

export interface CabUpdatesSuccessResponse {
	data: CabUpdateResponse;
}

export interface BaseBookingDetail {
	bookingId: string;
	bookingStatus: BookingStatus;
	/**
	 * Whether the booking is rescheduled or not
	 */
	reschedule?: boolean;
	invoiceUrl: string;
	eticketUrl: string;
	advanceReceiptUrl?: string;
	commuteMsg: string;

	/**
	 * TODO: not finalised yet. pending from backend.
	 */
	coupon?: {
		couponCode: string;
		benefits: {
			instantDiscount?: number;
			cashback?: number;
		};
	};
	pendingInfo?: {
		msg: string; // html string
		durationMs?: number;
		startTimeEpoch?: number;
	};
}
export interface CancellationDetail {
	allowed: boolean;
	msg: string; // html string
}

export interface RescheduleDetail {
	allowed: boolean;
	note: string; // html string
	rescheduleCount?: number;
}

export type RefundStatus =
	| 'COMPLETED'
	| 'IN_PROGRESS'
	| 'REJECTED'
	| 'INITIATED';

export interface RefundTimelineItem {
	title: string;
	subtitle: string;
	status: RefundStatus;
}

export type LabelIcon =
	| 'IxigoMoney'
	| 'IxigoMoneyFilled'
	| 'WalletFilled'
	| 'BankFilled'
	| 'CreditCard';

export interface RefundSummaryItem {
	label: {
		text: string;
		color?: string;
		icon?: LabelIcon;
	};
	value: {
		text: string;
		color?: string;
		icon?: LabelIcon;
	};
	amount: number;
}

export interface RefundBreakUp {
	netPay: RefundSummaryItem[];
	charges: RefundSummaryItem[];
	adjustedAmount?: RefundSummaryItem[];
	discounts: RefundSummaryItem[];
	deductions: RefundSummaryItem[];
}

export type RefundSummaryMode =
	| 'MONEY'
	| 'MONEY_MAX'
	| 'ORIGINAL_MODE_PAYMENT'
	| 'OTHERS'
	| 'UPI'
	| 'NETBANKING'
	| 'CARD';

export interface RefundSummary {
	title: string;
	modes: {
		amount: number;
		currency?: string;
		chip: string;
		label: string;
		note?: string;
		type: RefundSummaryMode;
	}[];
}

export interface RefundDetail {
	msg: string; // html string
	timeline: RefundTimelineItem[];
	breakUps: RefundBreakUp;
	refundSummary: RefundSummary;
}
export interface EmergencyContact {
	sosPhoneNumber: string;
	ixigoPhoneNumber: string;
}

export interface RescheduleRefundDetail {
	text: string;
	subText: string;
}

export interface BookingSuccessResponse {
	data: {
		heading: Heading;
		farePolicies: BaseFarePolicyDetail;
		rideDetails: RideDetails;
		cabDetail: Omit<BaseCabItemOption, 'interpolationVariables'> & {
			imageUrl: string;
		};
		contactDetails: BaseContactDetail;
		bookingDetail: BaseBookingDetail;
		cancellationDetail?: CancellationDetail;
		rescheduleDetail?: RescheduleDetail;
		refundDetail?: RefundDetail;
		extraPaymentInfo?: {
			amountToBePaid: BaseFareSummary;
			displayFares: BaseFareSummary[];
		};
		rescheduleRefundDetail?: RescheduleRefundDetail;
		parentBookingId?: string;
	};
}

export type TripErrorType =
	| 'INVALID_BOOKING_ID'
	| 'API_ERROR'
	| 'RETURN_HOME'
	| 'RETRY'
	| 'GENERIC_ERROR';
export type BookingErrorResponse = ApiErrorResponse<TripErrorType>;
export type BookingResponse = BookingSuccessResponse | BookingErrorResponse;

/**
 * Fetches booking detail data
 * Currently returns mock data for development
 *
 * @param bookingId - Unique identifier for the booking
 * @returns Promise with booking data or error response
 */
export const getBookingDetail = async (
	bookingId: string,
): Promise<BookingResponse> => {
	return executeApiCall(
		() =>
			fetch
				.post<BookingResponse>('cabs/api/v1/tripDetails', {
					body: JSON.stringify({
						bookingId,
					}),
					headers: {
						'Content-Type': 'application/json',
					},
				})
				.json(),
		`getBookingDetail:${bookingId}`,
	);
};

export const getCabUpdates = async (
	bookingId: string,
	options?: { signal?: AbortSignal },
): Promise<CabUpdatesSuccessResponse | BookingErrorResponse> => {
	return executeApiCall(
		() =>
			fetch
				.post<CabUpdatesSuccessResponse>('cabs/api/v1/cabUpdates', {
					body: JSON.stringify({
						bookingId,
					}),
					headers: {
						'Content-Type': 'application/json',
					},
					signal: options?.signal,
				})
				.json(),
		`getCabUpdates:${bookingId}`,
	);
};
